import { getTrackingService, cdnService, setWallet, setCurrencyMultipliers, setInterpolationParams, setUiBackgroundConfig, setMobile, setTablet, setDesktop, isMobileQuery, isTabletQuery, isDesktopQuery, queryTranslations, debounce, getFeatureEnabled, getFeatureValueV2, configQuery, modalService } from '@gig/magic-common';
import { LANGUAGE_UPDATED, USER_LOGGED_IN_UPDATED, Feature } from '@gig/magic-messages';
import { CssBreakpointsV2Service } from './services/breakpoint/breakpoint.service.js';
import { __awaiter, __generator } from 'tslib';
import { magic } from '@gig/magic-sdk';
import './services/config/toolkit-config.interface.js';
import { ConfigV2Service } from './services/config/config-v2.service.js';
import { CookieService } from './services/cookie/cookie.service.js';
import { AffiliateService } from './services/affiliate/affiliate.service.js';
import './services/country/country.constants.js';
import { CountryService } from './services/country/country.service.js';
import { DeviceV2Service } from './services/device/device.service.js';
import { BalanceService } from './services/balance/balance.service.js';
import { UserPlayControlService } from './services/user/user.play-control.service.js';
import { UserSessionService } from './services/user/user-session.service.js';
import './services/jurisdiction/jurisdiction.utils.js';
import { JurisdictionService } from './services/jurisdiction/jurisdiction.service.js';
import { JurisdictionCoreXService } from './services/jurisdiction/jurisdiction-corex.service.js';
import { JurisdictionModulesService } from './services/jurisdiction/jurisdiction-modules.service.js';
import { ExperimentService } from './services/experiment/experiment.service.js';
import { LanguageService } from './services/language/language.service.js';
import { TranslationService } from './services/translation/translation.service.js';
import { TrackingService } from './services/tracking/tracking.service.js';
import { FeatureService } from './services/features/features.service.js';
import { GamesService } from './services/games/games.service.js';
import { GamesListService } from './services/games/games-list.service.js';
import { UserRecommendedGamesService } from './services/games/user-recommended-games.service.js';
import { GamesFetchService } from './services/games/games-fetch.service.js';
import { GamesFetchCoreXService } from './services/games/games-fetch-corex.service.js';
import { GameSessionService } from './services/game-session/game-session.service.js';
import { MessagingService } from './services/messaging/messaging.service.js';
import { GameplayService } from './services/gameplay/gameplay.service.js';
import { AmlService } from './services/aml/aml.service.js';
import { AccountService } from './services/account/account.service.js';
import { PepBlockService } from './services/account/pep-block.service.js';
import { NetworkConnectionStatusService } from './services/network-connection-status/network-connection-status.service.js';
import { PageNotFoundService } from './services/page-not-found/page-not-found.js';
import { KeycloakService } from './services/keycloak/keycloak.service.js';
import './services/mobile-app/mobile-app.types.js';
import { MobileAppService } from './services/mobile-app/mobile-app.service.js';
import './services/mobile-app/mobile-app.utils.js';
import { MobileAppAuthService } from './services/mobile-app/mobile-app-auth.service.js';
import { MobileAppWSDriver } from './services/mobile-app/mobile-app-ws-driver.js';
import { TransportType } from '@gig/magic-sdk/dist/transport';
import { setLoyaltyInterpolationParams, setMetaInformation, setUserInterpolationParams, initWakeLock } from './utils/toolkit.utils.js';
import { GeoLocationService } from './services/geo-location/geo-location.service.js';
import { CloudflareService } from './services/cloudflare/cloudflare.service.js';
import { AmlCorexService } from './services/aml/aml-corex.service.js';

var Toolkit = /** @class */ (function () {
    function Toolkit() {
        this.cookieService = null;
        this.countryService = null;
        this.affiliateService = null;
        this.deviceService = null;
        this.keycloakService = null;
        this.mobileAppService = null;
        this.mobileAppAuthService = null;
        this.subscriptions = [];
        this.user = null;
        this.isCoreX = false;
        /**
         * As magic warned, there are cases in which multiple messages are sent
         * almost at the same time. We debounce them, just in case
         */
        this.debouncedLoyaltyPointsUpdate = debounce(this.handleLoyaltyPointsUpdate, 300);
    }
    Toolkit.prototype.bootstrap = function (config, countryToJurisdiction, defaultJurisdiction) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.isCoreX = !!config.keycloak;
                        // pass frontend config to the service when instantiating it
                        new ConfigV2Service(config);
                        modalService.initialise();
                        this.mobileAppService = new MobileAppService(config.nativeAppEnabled);
                        MobileAppWSDriver.mobileAppService = this.mobileAppService;
                        return [4 /*yield*/, magic.setup({
                                key: config.magic.apiToken,
                                cdnUrl: config.magic.cdnUrl,
                                httpUrl: config.magic.httpUrl,
                                wsUrl: config.magic.wsUrl,
                                transport: TransportType.BOTH,
                                recordLastActivity: true,
                                wsReconnect: true,
                                brand: config.magic.brand,
                                isCoreX: this.isCoreX,
                                wsDriver: this.mobileAppService.isMobileApp
                                    ? MobileAppWSDriver
                                    : undefined,
                            })];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.waitForSession(config)];
                    case 2:
                        _a.sent();
                        this.checkMobileAppActiveCurrency();
                        this.setUpConfiguration(config);
                        new NetworkConnectionStatusService();
                        this.initTracking();
                        new UserSessionService();
                        this.cookieService = new CookieService();
                        // register country query handler
                        this.countryService = new CountryService(this.cookieService);
                        // register affiliate tag query handler
                        this.affiliateService = new AffiliateService(this.cookieService);
                        this.deviceService = new DeviceV2Service();
                        new LanguageService();
                        new CssBreakpointsV2Service(config.cssBreakpoints, this.deviceService);
                        new BalanceService();
                        new ExperimentService(config.experiments);
                        new GameSessionService();
                        new GamesListService();
                        new GameplayService();
                        // magic sdk needs to be setup when this runs
                        new TranslationService();
                        this.syncMetaInformation();
                        new AccountService();
                        new MessagingService();
                        new UserPlayControlService();
                        new PepBlockService();
                        new FeatureService(this.countryService, this.deviceService);
                        if (this.isCoreX) {
                            new GamesFetchCoreXService(new GamesService());
                            new JurisdictionCoreXService(countryToJurisdiction, defaultJurisdiction);
                            new AmlCorexService();
                        }
                        else {
                            new GamesFetchService(new GamesService());
                            new JurisdictionService(countryToJurisdiction, defaultJurisdiction);
                            new AmlService();
                        }
                        new JurisdictionModulesService(countryToJurisdiction, defaultJurisdiction);
                        new UserRecommendedGamesService();
                        new PageNotFoundService();
                        return [4 /*yield*/, this.setUpDeviceInfo()];
                    case 3:
                        _a.sent();
                        getFeatureEnabled(Feature.WAKE_LOCK).then(function (wakeLockEnabled) {
                            if (wakeLockEnabled) {
                                initWakeLock();
                            }
                        });
                        this.syncUserInterpolationParams();
                        new CloudflareService();
                        getFeatureValueV2(Feature.GTM_ID).then(function (cmsGtmData) {
                            var _a = cmsGtmData
                                ? cmsGtmData.split(',', 2)
                                : [], cmsGtmId = _a[0], cmsExtraQueryParams = _a[1];
                            return configQuery('gtmContainerId').then(function (configGtmId) {
                                var gtmId = cmsGtmId || configGtmId;
                                if (gtmId) {
                                    _this.injectGoogleTagManager(gtmId, cmsExtraQueryParams);
                                }
                            });
                        });
                        return [2 /*return*/, new GeoLocationService(this.mobileAppService).waitForPermission()];
                }
            });
        });
    };
    Object.defineProperty(Toolkit.prototype, "affiliate", {
        get: function () {
            if (!this.affiliateService) {
                throw new Error('Affiliate service is not available, did you run toolkit.bootstrap()?');
            }
            return this.affiliateService;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * `waitForSession` does the following things:
     * - re-establishes the user session, if user was already logged in
     * - connects to web socket (the url needs the session id generated earlier)
     * - subscribes to user tags (needs the user object to be set up first)
     *
     * @param config Contains the `sessionExpireTime` config (Core only)
     * @private
     */
    Toolkit.prototype.waitForSession = function (config) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var user;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!((_a = this.mobileAppService) === null || _a === void 0 ? void 0 : _a.isMobileApp)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.waitForMobileAppSession()];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 2:
                        if (!this.isCoreX) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.waitForKeycloakSession(config)];
                    case 3:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 4:
                        magic.session.setKeycloakSession(null);
                        return [4 /*yield*/, this.waitForMagicSession(config)];
                    case 5:
                        _b.sent();
                        _b.label = 6;
                    case 6: return [4 /*yield*/, magic.connectWsDriver()];
                    case 7:
                        _b.sent();
                        user = magic.session.getUser();
                        if (user) {
                            magic.methods.users.subscribeForTags(user);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.waitForKeycloakSession = function (config) {
        this.keycloakService = new KeycloakService();
        return this.keycloakService.init(config);
    };
    Toolkit.prototype.waitForMobileAppSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.mobileAppAuthService = new MobileAppAuthService(this.mobileAppService);
                return [2 /*return*/, this.mobileAppAuthService.init()];
            });
        });
    };
    /**
     * For an efficient initialisation of the session, if the last API call (which keeps a session alive)
     * was made before the known expiration time of a session, we will resolve the wait immediately
     * assuming the last known session status is still valid and then check the real status in the background.
     *
     * If the last API call was done longer than the expiration time, we will block the load until we hear back
     * from the API the real status of the session as chances are that the session was expired.
     *
     * If the user was not previously logged in, we don't need to check anything
     */
    Toolkit.prototype.waitForMagicSession = function (config) {
        return __awaiter(this, void 0, void 0, function () {
            var currentTime, lastActivityTime;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!magic.session.getUser()) {
                            return [2 /*return*/];
                        }
                        currentTime = new Date().getTime();
                        lastActivityTime = magic.getLastRequestTimestamp();
                        if (!(!lastActivityTime ||
                            currentTime - lastActivityTime > config.sessionExpireTime)) return [3 /*break*/, 2];
                        return [4 /*yield*/, magic.methods.users.resumeLoginV2()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                    case 2:
                        magic.methods.users.resumeLoginV2();
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.injectGoogleTagManager = function (gtmContainerId, cmsExtraQueryParams) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var isMobileAppTrackingEnabled, _c, script;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!((_a = this.mobileAppService) === null || _a === void 0 ? void 0 : _a.isMobileApp)) return [3 /*break*/, 2];
                        return [4 /*yield*/, ((_b = this.mobileAppService) === null || _b === void 0 ? void 0 : _b.isTrackingEnabled())];
                    case 1:
                        _c = _d.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _c = true;
                        _d.label = 3;
                    case 3:
                        isMobileAppTrackingEnabled = _c;
                        if (!isMobileAppTrackingEnabled)
                            return [2 /*return*/];
                        if (!gtmContainerId)
                            return [2 /*return*/];
                        if (document.getElementById('gtm-script'))
                            return [2 /*return*/];
                        script = document.createElement('script');
                        script.id = 'gtm-script';
                        script.async = true;
                        script.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmContainerId + (cmsExtraQueryParams ? cmsExtraQueryParams : '');
                        document.head.appendChild(script);
                        if (!window.dataLayer) {
                            window.dataLayer = [];
                        }
                        window.dataLayer.push({
                            'gtm.start': new Date().getTime(),
                            'event': 'gtm.js',
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.checkMobileAppActiveCurrency = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!((_a = this.mobileAppService) === null || _a === void 0 ? void 0 : _a.isMobileApp)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.mobileAppService.getCurrentCurrency()];
                    case 1:
                        _b.sent();
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.initTracking = function () {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var isMobileAppTrackingEnabled, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!window.dataLayer) {
                            window.dataLayer = [];
                        }
                        if (!((_a = this.mobileAppService) === null || _a === void 0 ? void 0 : _a.isMobileApp)) return [3 /*break*/, 2];
                        return [4 /*yield*/, ((_b = this.mobileAppService) === null || _b === void 0 ? void 0 : _b.isTrackingEnabled())];
                    case 1:
                        _c = _d.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _c = true;
                        _d.label = 3;
                    case 3:
                        isMobileAppTrackingEnabled = _c;
                        if (!isMobileAppTrackingEnabled)
                            return [2 /*return*/];
                        getTrackingService().onTrack(function (event) {
                            // reset the previous data layer fields
                            window.dataLayer.push(function () {
                                var _a;
                                (_a = this === null || this === void 0 ? void 0 : this.reset) === null || _a === void 0 ? void 0 : _a.call(this);
                            });
                            window.dataLayer.push(event);
                        });
                        new TrackingService(getTrackingService());
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.setUpConfiguration = function (config) {
        var _a, _b;
        magic.methods.games.setup({
            production: config.environment === 'production',
        });
        // Magic UI-Kit initialization
        cdnService.setup(config.magic.imgCdnUrl);
        // Set wallet config
        setWallet({
            merchant: (_a = config.wallet) === null || _a === void 0 ? void 0 : _a.merchant,
            paymentUrl: (_b = config.wallet) === null || _b === void 0 ? void 0 : _b.paymentUrl,
        });
        // Setting currency conversion values
        setCurrencyMultipliers(config.currencyMultipliers);
        // Set up base interpolation parameters for magic-ui-kit
        setInterpolationParams(config.interpolationParams);
        setUiBackgroundConfig(config.uiBackground);
    };
    Toolkit.prototype.setUpDeviceInfo = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, isMobile, isTablet, isDesktop;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, Promise.all([
                            isMobileQuery(),
                            isTabletQuery(),
                            isDesktopQuery(),
                        ])];
                    case 1:
                        _a = _b.sent(), isMobile = _a[0], isTablet = _a[1], isDesktop = _a[2];
                        setMobile(isMobile);
                        setTablet(isTablet);
                        setDesktop(isDesktop);
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.handleLoyaltyPointsUpdate = function (data) {
        setLoyaltyInterpolationParams(data.progress, data.level, data.points);
    };
    Toolkit.prototype.handleUserLoyalty = function () {
        return __awaiter(this, void 0, void 0, function () {
            var loyaltyPromise, loyaltyData;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        loyaltyPromise = magic.invokeCached(60, magic.methods.loyalty, 'userProgress')({
                            userId: this.user.userId,
                        });
                        return [4 /*yield*/, loyaltyPromise];
                    case 1:
                        loyaltyData = _a.sent();
                        setLoyaltyInterpolationParams(loyaltyData.progress, loyaltyData.level, loyaltyData.points);
                        this.subscriptions.push(magic.events.loyaltyPointsUpdate(function (data) {
                            return _this.debouncedLoyaltyPointsUpdate(data);
                        }).unsub);
                        return [2 /*return*/];
                }
            });
        });
    };
    Toolkit.prototype.syncMetaInformation = function () {
        queryTranslations().then(function () {
            setMetaInformation();
        });
        window.eventBus.subscribe(LANGUAGE_UPDATED, function () {
            setMetaInformation();
        });
    };
    Toolkit.prototype.syncUserInterpolationParams = function () {
        var _this = this;
        window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, function (e) {
            var isLoggedIn = !!e.payload;
            if (isLoggedIn) {
                _this.user = magic.session.getUser();
                if (!_this.isCoreX) {
                    _this.handleUserLoyalty();
                }
            }
            if (!isLoggedIn) {
                _this.subscriptions.forEach(function (unsub) { return unsub(); });
                _this.subscriptions = [];
            }
            setUserInterpolationParams();
        }, { replay: true });
    };
    return Toolkit;
}());
var toolkit = new Toolkit();

export { toolkit };
